import React, { useMemo, useState, useCallback } from "react";
import { 
  Box, 
  Paper, 
  Typography, 
  Chip, 
  Tooltip, 
  IconButton,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  Button
} from "@mui/material";
import { 
  Code, 
  Folder, 
  Settings, 
  Storage,
  Fullscreen,
  FullscreenExit,
  Close
} from "@mui/icons-material";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  MarkerType,
  applyNodeChanges,
  applyEdgeChanges,
  NodeChange,
  EdgeChange,
} from "reactflow";
import "reactflow/dist/style.css";

const ComponentDiagram = ({ componentStructure }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Check if component structure has existingFilePaths to determine file existence
  const existingFilePaths = useMemo(() => {
    if (componentStructure?.existingFilePaths) {
      return new Set(componentStructure.existingFilePaths.map(p => p.toLowerCase()));
    }
    return new Set();
  }, [componentStructure]);

  // Initialize nodes and edges from component structure
  const initialData = useMemo(() => {
    if (
      !componentStructure ||
      !componentStructure.components ||
      componentStructure.components.length === 0
    ) {
      return { nodes: [], edges: [] };
    }

    const { components, relationships } = componentStructure;

    // Create a map of components for easy lookup
    const componentMap = {};
    components.forEach((comp) => {
      componentMap[comp.name] = comp;
    });

    // Calculate layout positions using a simple hierarchical layout
    const getLayoutPositions = () => {
      const positions = {};
      const visited = new Set();
      const levelMap = {};
      let maxLevel = 0;

      // First, determine levels (depth from root nodes)
      const getLevel = (componentName, level = 0) => {
        if (visited.has(componentName)) return levelMap[componentName] || level;
        visited.add(componentName);

        // Find incoming relationships
        const incoming =
          relationships?.filter((rel) => {
            const toLower = (rel.to || "").toLowerCase();
            const compLower = componentName.toLowerCase();
            return (
              toLower === compLower ||
              toLower.includes(compLower) ||
              compLower.includes(toLower)
            );
          }) || [];

        if (incoming.length === 0) {
          levelMap[componentName] = 0;
          return 0;
        }

        const maxIncomingLevel = Math.max(
          ...incoming.map((rel) => {
            const fromComp = components.find((c) => {
              const fromLower = (rel.from || "").toLowerCase();
              const compLower = c.name.toLowerCase();
              return (
                fromLower === compLower ||
                fromLower.includes(compLower) ||
                compLower.includes(fromLower)
              );
            });
            return fromComp ? getLevel(fromComp.name, level + 1) : 0;
          }),
        );
        const calculatedLevel = maxIncomingLevel + 1;
        levelMap[componentName] = calculatedLevel;
        maxLevel = Math.max(maxLevel, calculatedLevel);
        return calculatedLevel;
      };

      components.forEach((comp) => {
        getLevel(comp.name);
      });

      // Group components by level
      const levelGroups = {};
      components.forEach((comp) => {
        const level = levelMap[comp.name] || 0;
        if (!levelGroups[level]) levelGroups[level] = [];
        levelGroups[level].push(comp.name);
      });

      // Calculate positions
      const nodeSpacing = 250;
      const levelSpacing = 300;
      const startX = 100;
      const startY = 100;

      components.forEach((comp) => {
        const level = levelMap[comp.name] || 0;
        const levelIndex = levelGroups[level].indexOf(comp.name);
        const levelSize = levelGroups[level].length;

        const x = startX + level * levelSpacing;
        const y =
          startY +
          levelIndex * nodeSpacing -
          ((levelSize - 1) * nodeSpacing) / 2;

        positions[comp.name] = { x, y };
      });

      return positions;
    };

    const positions = getLayoutPositions();

    // Helper function to normalize strings for matching
    const normalize = (str) => {
      if (!str) return "";
      return str
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " ")
        .replace(/[^a-z0-9\s]/g, "")
        .replace(
          /\b(front[- ]?end|frontend|ui|component|service|repository|dao|controller|api|endpoint|table|entity|model|class|file|module|script|job|worker|logic|engine|form|list|detail|override|override)\b/gi,
          "",
        )
        .trim();
    };

    // Helper function to extract key words from a string
    const extractKeyWords = (str) => {
      const normalized = normalize(str);
      const words = normalized
        .split(/[\s\/\-_]+/)
        .filter((w) => w.length > 2)
        .filter(
          (w) =>
            ![
              "the",
              "and",
              "or",
              "for",
              "with",
              "from",
              "to",
              "all",
              "modified",
            ].includes(w),
        );
      return words;
    };

    // Helper function to find component by name (exact or partial match with improved fuzzy matching)
    const findComponentByName = (name) => {
      if (!name) return null;

      // First try exact match
      if (componentMap[name]) {
        return componentMap[name];
      }

      // Try case-insensitive exact match
      const lowerName = name.toLowerCase().trim();
      for (const [compName, comp] of Object.entries(componentMap)) {
        if (compName.toLowerCase().trim() === lowerName) {
          return comp;
        }
      }

      // Normalize both names
      const normalizedName = normalize(name);

      // Try to find by normalized exact match
      for (const [compName, comp] of Object.entries(componentMap)) {
        const normalizedCompName = normalize(compName);
        if (normalizedCompName === normalizedName) {
          return comp;
        }
      }

      // Try substring matching (one contains the other)
      for (const [compName, comp] of Object.entries(componentMap)) {
        const normalizedCompName = normalize(compName);

        if (normalizedName.length < 3 || normalizedCompName.length < 3) {
          continue;
        }

        if (
          normalizedName.includes(normalizedCompName) ||
          normalizedCompName.includes(normalizedName)
        ) {
          return comp;
        }
      }

      // Try word-by-word matching with extracted keywords
      const nameKeywords = extractKeyWords(name);
      if (nameKeywords.length > 0) {
        let bestMatch = null;
        let bestScore = 0;

        for (const [compName, comp] of Object.entries(componentMap)) {
          const compKeywords = extractKeyWords(compName);
          if (compKeywords.length === 0) continue;

          const matchingKeywords = nameKeywords.filter((nk) =>
            compKeywords.some((ck) => ck.includes(nk) || nk.includes(ck)),
          );

          const score =
            matchingKeywords.length /
            Math.max(nameKeywords.length, compKeywords.length);

          if (score >= 0.5 && score > bestScore) {
            bestMatch = comp;
            bestScore = score;
          }
        }

        if (bestMatch) {
          return bestMatch;
        }
      }

      // Last resort: try matching any significant word
      const significantWords = nameKeywords.filter((w) => w.length >= 4);
      if (significantWords.length > 0) {
        for (const [compName, comp] of Object.entries(componentMap)) {
          const compNormalized = normalize(compName);
          for (const word of significantWords) {
            if (compNormalized.includes(word)) {
              return comp;
            }
          }
        }
      }

      return null;
    };

    // Create React Flow nodes
    const flowNodes = components.map((comp, index) => {
      const getIcon = (type) => {
        switch (type?.toLowerCase()) {
          case "file":
            return <Code fontSize="small" />;
          case "folder":
          case "module":
            return <Folder fontSize="small" />;
          case "service":
            return <Settings fontSize="small" />;
          case "database":
          case "storage":
            return <Storage fontSize="small" />;
          default:
            return <Code fontSize="small" />;
        }
      };

      const getImpactColor = (impactLevel) => {
        switch (impactLevel?.toLowerCase()) {
          case "high":
            return "#f44336";
          case "medium":
            return "#ff9800";
          case "low":
            return "#2196f3";
          default:
            return "#757575";
        }
      };

      const position = positions[comp.name] || { x: 100 + index * 250, y: 100 };

      return {
        id: comp.name,
        type: "default",
        position: { x: position.x, y: position.y },
        data: {
          label: (
            <Paper
              elevation={3}
              sx={{
                p: 1.5,
                minWidth: isMobile ? 150 : isTablet ? 200 : 250,
                maxWidth: isMobile ? 200 : isTablet ? 280 : 320,
                width: "100%",
                borderLeft: `4px solid ${getImpactColor(comp.impact_level)}`,
                bgcolor: "background.paper",
                "&:hover": {
                  boxShadow: 6,
                },
                overflow: "hidden",
              }}
            >
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                {getIcon(comp.type)}
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{
                    fontSize: "0.85rem",
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {comp.name}
                </Typography>
              </Box>
              {comp.type && (
                <Chip
                  label={comp.type}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.65rem", mb: 0.5 }}
                />
              )}
              {comp.impact_level && (
                <Chip
                  label={`${comp.impact_level.toUpperCase()}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    bgcolor: getImpactColor(comp.impact_level),
                    color: "white",
                    ml: 0.5,
                  }}
                />
              )}
              {comp.path && (
                <Tooltip title={comp.path} arrow>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ 
                      mt: 0.5, 
                      fontSize: "0.7rem",
                      wordBreak: "break-all",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    📁 {comp.path}
                  </Typography>
                </Tooltip>
              )}
              {/* File existence indicator */}
              {comp.path && (
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  {(() => {
                    // Check if file exists by comparing path with existingFilePaths
                    const pathLower = comp.path.toLowerCase();
                    const fileExists = existingFilePaths.size > 0 && existingFilePaths.has(pathLower);
                    const isNewFile = existingFilePaths.size > 0 && !fileExists;
                    
                    // Also check explicit exists field if present
                    const explicitExists = comp.exists !== undefined 
                      ? (comp.exists === true || comp.exists === 'true')
                      : null;
                    
                    const finalExists = explicitExists !== null ? explicitExists : fileExists;
                    const finalIsNew = explicitExists !== null ? !explicitExists : isNewFile;
                    
                    if (finalIsNew) {
                      return (
                        <Chip
                          label="New File"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            bgcolor: "#4caf50",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      );
                    } else if (finalExists) {
                      return (
                        <Chip
                          label="Exists"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            bgcolor: "#2196f3",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      );
                    }
                    return null;
                  })()}
                </Box>
              )}
              {comp.description && (
                <Tooltip title={comp.description}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5, fontSize: "0.7rem" }}
                  >
                    {comp.description.length > 50
                      ? `${comp.description.substring(0, 50)}...`
                      : comp.description}
                  </Typography>
                </Tooltip>
              )}
            </Paper>
          ),
        },
        style: {
          background: "transparent",
          border: "none",
        },
      };
    });

    // Create React Flow edges (links)
    const flowEdges = [];
    (relationships || []).forEach((rel, index) => {
      const fromComp = findComponentByName(rel.from);
      const toComp = findComponentByName(rel.to);

      if (fromComp && toComp) {
        flowEdges.push({
          id: `edge-${index}`,
          source: fromComp.name,
          target: toComp.name,
          label: rel.type || "related",
          type: "smoothstep",
          animated: false,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
          },
          style: {
            stroke: "#6366f1",
            strokeWidth: 2,
          },
          labelStyle: {
            fill: "#6366f1",
            fontWeight: 600,
            fontSize: 12,
          },
        });
      }
    });

    return { nodes: flowNodes, edges: flowEdges };
  }, [componentStructure]);

  // Get a unique key for this diagram based on component structure
  const diagramKey = useMemo(() => {
    if (!componentStructure?.components) return null;
    const componentNames = componentStructure.components
      .map((c) => c.name)
      .sort()
      .join(",");
    return `diagram-${componentNames}`;
  }, [componentStructure]);

  // Load saved positions from localStorage
  const loadSavedPositions = () => {
    if (!diagramKey) return null;
    try {
      const saved = localStorage.getItem(diagramKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn("Failed to load saved diagram positions:", e);
    }
    return null;
  };

  // Apply saved positions to nodes
  const applySavedPositions = (nodes, savedPositions) => {
    if (!savedPositions) return nodes;
    return nodes.map((node) => {
      if (savedPositions[node.id]) {
        return { ...node, position: savedPositions[node.id] };
      }
      return node;
    });
  };

  // State to manage node positions (allows dragging)
  const [nodes, setNodes] = useState(() => {
    const savedPositions = loadSavedPositions();
    return applySavedPositions(initialData.nodes, savedPositions);
  });
  const [edges, setEdges] = useState(initialData.edges);

  // Update nodes/edges when componentStructure changes
  React.useEffect(() => {
    const savedPositions = loadSavedPositions();
    setNodes(applySavedPositions(initialData.nodes, savedPositions));
    setEdges(initialData.edges);
  }, [initialData, diagramKey]);

  // Save positions to localStorage when nodes change
  React.useEffect(() => {
    if (!diagramKey || nodes.length === 0) return;

    const positions = {};
    nodes.forEach((node) => {
      positions[node.id] = node.position;
    });

    try {
      localStorage.setItem(diagramKey, JSON.stringify(positions));
    } catch (e) {
      console.warn("Failed to save diagram positions:", e);
    }
  }, [nodes, diagramKey]);

  // Handle node changes (dragging, etc.)
  const onNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes],
  );

  // Handle edge changes
  const onEdgesChange = useCallback(
    (changes) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges],
  );

  if (
    !componentStructure ||
    !componentStructure.components ||
    componentStructure.components.length === 0
  ) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No component structure data available
        </Typography>
      </Paper>
    );
  }

  if (nodes.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No components available to display
        </Typography>
      </Paper>
    );
  }

  // Calculate responsive height
  const getDiagramHeight = () => {
    if (isMobile) return 400;
    if (isTablet) return 500;
    return 600;
  };

  // Render the ReactFlow diagram
  const renderDiagram = (height) => (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      fitView
      attributionPosition="bottom-left"
      nodesDraggable={true}
      nodesConnectable={false}
      elementsSelectable={true}
      style={{
        width: "100%",
        height: height || "100%",
      }}
    >
      <Background color="#e0e0e0" gap={16} />
      <Controls />
      <MiniMap
        nodeColor={(node) => "#6366f1"}
        maskColor="rgba(0, 0, 0, 0.1)"
        style={{
          backgroundColor: "#f5f5f5",
          border: "1px solid #ddd",
        }}
        pannable
        zoomable
      />
    </ReactFlow>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">
          Component Relationships
        </Typography>
        <IconButton
          onClick={() => setIsFullscreen(true)}
          size="small"
          sx={{ ml: 2 }}
          aria-label="Expand diagram to full screen"
          color="primary"
        >
          <Fullscreen />
        </IconButton>
      </Box>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mt: 2,
          bgcolor: "grey.50",
          minHeight: getDiagramHeight(),
          height: getDiagramHeight(),
          position: "relative",
          overflow: "hidden",
        }}
      >
        {renderDiagram(getDiagramHeight())}
      </Paper>
      
      {/* Fullscreen Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            m: 0,
            width: "100vw",
            height: "100vh",
            maxHeight: "100vh",
            maxWidth: "100vw",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6">Component Relationships - Full Screen</Typography>
          <Box>
            <IconButton
              onClick={() => setIsFullscreen(false)}
              size="small"
              aria-label="Close full screen"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 2,
            bgcolor: "grey.50",
            height: "calc(100vh - 80px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {renderDiagram("calc(100vh - 120px)")}
        </DialogContent>
      </Dialog>
      {edges.length < (componentStructure.relationships?.length || 0) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {edges.length} of {componentStructure.relationships?.length || 0}{" "}
          relationships displayed. Some relationships could not be matched to
          components.
        </Typography>
      )}
    </Box>
  );
};

export default ComponentDiagram;
